import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { reportTextToHtml } from '../utils/reportUtils';

interface RichReportEditorProps {
  /** Plain-text report content (sections already rendered as text) */
  value: string;
  /** Called with plain-text when user saves */
  onChange: (value: string) => void;
  /** Whether the editor is in edit mode (toolbar active, content editable) */
  editing: boolean;
  /** Font size zoom level (1 = 100%) */
  fontSize?: number;
  onFontSizeChange?: (size: number) => void;
}

/** Convert contentEditable innerHTML back to plain text */
function htmlToText(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;

  // Walk block elements and collect text lines
  const lines: string[] = [];
  const blocks = div.querySelectorAll('p, div, li, br, h1, h2, h3, h4, h5, h6');
  if (blocks.length === 0) {
    return div.textContent ?? '';
  }

  // Use a temporary DOM walker
  const walker = document.createTreeWalker(div, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let currentLine = '';

  const flush = () => {
    if (currentLine !== '') {
      lines.push(currentLine);
      currentLine = '';
    }
  };

  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      currentLine += node.textContent ?? '';
    } else {
      const el = node as Element;
      const tag = el.tagName?.toLowerCase();
      if (['p', 'div', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) {
        flush();
      } else if (tag === 'br') {
        flush();
      }
    }
    node = walker.nextNode();
  }
  flush();

  return lines
    .map((l) => l.trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const FONT_SIZES = [0.75, 0.8, 0.875, 0.95, 1.05, 1.15];
const DEFAULT_FONT_IDX = 2; // 0.875rem

const RichReportEditor: React.FC<RichReportEditorProps> = ({
  value,
  onChange,
  editing,
  fontSize,
  onFontSizeChange,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [fontIdx, setFontIdx] = useState(DEFAULT_FONT_IDX);
  const currentFontSize = fontSize ?? FONT_SIZES[fontIdx];

  // Populate editor when value or editing mode changes
  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = reportTextToHtml(value);
  }, [value, editing]);

  const exec = useCallback((command: string, value?: string) => {
    if (!editing) return;
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  }, [editing]);

  const handleInput = useCallback(() => {
    if (!editorRef.current || !editing) return;
    onChange(htmlToText(editorRef.current.innerHTML));
  }, [editing, onChange]);

  const zoomIn = () => {
    const next = Math.min(fontIdx + 1, FONT_SIZES.length - 1);
    setFontIdx(next);
    onFontSizeChange?.(FONT_SIZES[next]);
  };

  const zoomOut = () => {
    const next = Math.max(fontIdx - 1, 0);
    setFontIdx(next);
    onFontSizeChange?.(FONT_SIZES[next]);
  };

  const toolbarBtn = (
    title: string,
    icon: React.ReactNode,
    onClick: () => void,
    disabled = false,
  ) => (
    <Tooltip title={title} key={title}>
      <span>
        <IconButton
          size="small"
          onMouseDown={(e) => {
            e.preventDefault(); // don't blur editor
            if (!disabled) onClick();
          }}
          disabled={disabled || !editing}
          sx={{
            p: 0.25,
            borderRadius: 0.5,
            color: disabled || !editing ? 'text.disabled' : 'text.primary',
            '&:hover': { bgcolor: editing ? 'action.hover' : 'transparent' },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Formatting toolbar ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: 0,
          px: 0.5,
          py: 0,
          minHeight: 28,
          bgcolor: (theme) => editing
            ? theme.palette.grey[900]
            : theme.palette.mode === 'dark'
              ? theme.palette.grey[800]
              : theme.palette.grey[100],
          borderRadius: '4px 4px 0 0',
          border: '1px solid',
          borderColor: editing ? 'grey.700' : 'divider',
          transition: 'background-color 0.2s',
          overflowX: 'hidden',
        }}
      >
        {toolbarBtn('Bold (Ctrl+B)', <FormatBoldIcon sx={{ fontSize: 14 }} />, () => exec('bold'))}
        {toolbarBtn('Italic (Ctrl+I)', <FormatItalicIcon sx={{ fontSize: 14 }} />, () => exec('italic'))}
        {toolbarBtn('Underline (Ctrl+U)', <FormatUnderlinedIcon sx={{ fontSize: 14 }} />, () => exec('underline'))}

        <Box sx={{ width: '1px', height: 14, bgcolor: 'divider', mx: 0.5, flexShrink: 0 }} />

        {toolbarBtn('Bulleted list', <FormatListBulletedIcon sx={{ fontSize: 14 }} />, () => exec('insertUnorderedList'))}
        {toolbarBtn('Numbered list', <FormatListNumberedIcon sx={{ fontSize: 14 }} />, () => exec('insertOrderedList'))}

        <Box sx={{ width: '1px', height: 14, bgcolor: 'divider', mx: 0.5, flexShrink: 0 }} />

        {toolbarBtn('Undo (Ctrl+Z)', <UndoIcon sx={{ fontSize: 14 }} />, () => exec('undo'))}
        {toolbarBtn('Redo (Ctrl+Y)', <RedoIcon sx={{ fontSize: 14 }} />, () => exec('redo'))}

        <Box sx={{ width: '1px', height: 14, bgcolor: 'divider', mx: 0.5, flexShrink: 0 }} />

        {toolbarBtn('Clear formatting', <FormatClearIcon sx={{ fontSize: 14 }} />, () => exec('removeFormat'))}

        <Box sx={{ flex: 1 }} />

        {/* Zoom controls */}
        <Tooltip title="Zoom out">
          <span>
            <IconButton
              size="small"
              onMouseDown={(e) => { e.preventDefault(); zoomOut(); }}
              disabled={fontIdx === 0}
              sx={{ p: 0.25, color: 'text.secondary' }}
            >
              <ZoomOutIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', minWidth: 30, textAlign: 'center', fontSize: '0.6rem', flexShrink: 0 }}
        >
          {Math.round(currentFontSize * 100)}%
        </Typography>
        <Tooltip title="Zoom in">
          <span>
            <IconButton
              size="small"
              onMouseDown={(e) => { e.preventDefault(); zoomIn(); }}
              disabled={fontIdx === FONT_SIZES.length - 1}
              sx={{ p: 0.25, color: 'text.secondary' }}
            >
              <ZoomInIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── Editable content area ── */}
      <Box
        ref={editorRef}
        contentEditable={editing}
        suppressContentEditableWarning
        onInput={handleInput}
        spellCheck={editing}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          py: 1,
          outline: 'none',
          fontSize: `${currentFontSize}rem`,
          lineHeight: 1.6,
          fontFamily: 'inherit',
          color: 'text.primary',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderTop: 'none',
          borderColor: editing ? 'primary.main' : 'divider',
          borderRadius: '0 0 4px 4px',
          cursor: editing ? 'text' : 'default',
          userSelect: editing ? 'text' : 'none',
          transition: 'border-color 0.2s',
          '& p': { margin: '0 0 4px 0' },
          '& strong': { fontWeight: 700 },
          '& ul, & ol': { pl: 2.5, mt: 0.5, mb: 0.5 },
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
        }}
      />
    </Box>
  );
};

export default RichReportEditor;
